import { User, UserCheck, Sparkles } from "lucide-react";
import { writeGender } from "../lib/gender";

/**
 * GenderModal — beautiful, inviting Hebrew gender-preference picker.
 * Shown ONCE at the very start of the customer journey (when the visitor
 * clicks "Build New Automation" on the homepage).  The choice is stored
 * in localStorage and used by every greeting/copy across the app.
 *
 * Props:
 *  - open: boolean
 *  - onSelect(gender): callback fired after a pick (incl. "neutral")
 */
export function GenderModal({ open, onSelect }) {
  if (!open) return null;
  const pick = (g) => {
    writeGender(g);
    onSelect?.(g);
  };
  return (
    <div className="fixed inset-0 z-[10010] bg-stone-900/55 backdrop-blur-md flex items-center justify-center p-4 animate-fade-up" data-testid="global-gender-modal" dir="rtl">
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 sm:p-8 text-center">
        {/* Soft hero icon */}
        <div className="relative inline-flex h-20 w-20 mx-auto rounded-3xl bg-gradient-to-br from-fuchsia-500 via-rose-400 to-amber-400 items-center justify-center mb-5 shadow-lg shadow-fuchsia-200">
          <Sparkles className="h-9 w-9 text-white" />
          <div className="absolute inset-0 rounded-3xl bg-white/15 mix-blend-overlay" />
        </div>

        <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-stone-900 mb-1">
          שלום! איך נפנה אליך?
        </h3>
        <p className="text-sm text-stone-500 mb-6 leading-relaxed">
          בחר/י את לשון הפנייה הנוחה לך — נדבר איתך כך לאורך כל האפיון.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => pick("male")}
            className="group bg-gradient-to-br from-sky-50 to-blue-100 hover:from-sky-100 hover:to-blue-200 border-2 border-sky-300 hover:border-sky-500 rounded-2xl p-5 transition-all hover:scale-[1.03] hover:shadow-lg"
            data-testid="global-gender-male"
          >
            <User className="h-11 w-11 mx-auto text-sky-600 mb-2" />
            <div className="font-extrabold text-lg text-sky-900">בלשון זכר</div>
            <div className="text-[11px] text-sky-700 mt-1">״ברוך הבא״, ״תיאר לנו״</div>
          </button>
          <button
            type="button"
            onClick={() => pick("female")}
            className="group bg-gradient-to-br from-pink-50 to-rose-100 hover:from-pink-100 hover:to-rose-200 border-2 border-pink-300 hover:border-pink-500 rounded-2xl p-5 transition-all hover:scale-[1.03] hover:shadow-lg"
            data-testid="global-gender-female"
          >
            <UserCheck className="h-11 w-11 mx-auto text-pink-600 mb-2" />
            <div className="font-extrabold text-lg text-pink-900">בלשון נקבה</div>
            <div className="text-[11px] text-pink-700 mt-1">״ברוכה הבאה״, ״תארי לנו״</div>
          </button>
        </div>
        <button
          type="button"
          onClick={() => pick("neutral")}
          className="mt-5 text-xs text-stone-400 hover:text-stone-600 underline"
          data-testid="global-gender-skip"
        >
          דלג — השתמש בלשון ניטרלית
        </button>
      </div>
    </div>
  );
}
